import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, LogOut, Trash2, ShieldCheck } from "lucide-react";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import { useAuthStore } from "../../store/authStore";
import { apiClient } from "../../services/api";
import ChangePasswordSheet from "../../components/settings/ChangePasswordSheet";
import DeleteAccountModal from "../../components/settings/DeleteAccountModal";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuthStore();
  const [proStatus, setProStatus] = useState(null); // 'verified' | 'pending' | 'rejected' | 'not_applied'
  const [hasPassword, setHasPassword] = useState(true);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Detect professional status so we can hide the Trainer Portal link for non-pros
  // and adjust the "Become a Verified Professional" copy contextually.
  useEffect(() => {
    apiClient
      .get("/professionals/me")
      .then((r) => {
        if (r.data?.exists) setProStatus(r.data.application?.status || "pending");
        else setProStatus("not_applied");
      })
      .catch(() => setProStatus("not_applied"));
  }, []);

  // Infer whether the current user has a password (for Change Password UX)
  useEffect(() => {
    setHasPassword(user?.auth_provider !== "google");
  }, [user]);

  const isVerifiedPro = proStatus === "verified";
  const isGoogle = user?.auth_provider === "google";

  const sections = [
    {
      title: "Account",
      items: [
        { label: "Edit Profile", to: "/profile-setup", testid: "setting-edit-profile" },
        { label: "Messages", to: "/inbox", testid: "setting-inbox" },
        { label: "My Orders", to: "/marketplace/orders", testid: "setting-my-orders" },
        { label: "My Listings", to: "/marketplace/my-listings", testid: "setting-my-listings" },
        { label: "My Plans", to: "/me/plans", testid: "setting-my-plans" },
        !isVerifiedPro && {
          label:
            proStatus === "pending"
              ? "Application under review"
              : proStatus === "rejected"
              ? "Application not approved"
              : "Become a Verified Professional",
          to: proStatus === "pending" || proStatus === "rejected" ? "/pro/portal" : "/pro/apply",
          testid: "become-pro",
        },
        isVerifiedPro && {
          label: "My Trainer Portal",
          to: "/pro/portal",
          testid: "settings-portal-link",
          rightChip: "VERIFIED",
        },
        {
          label: hasPassword ? "Change Password" : "Set a Password",
          action: () => setShowChangePw(true),
          testid: "setting-change-password",
        },
      ].filter(Boolean),
    },
    {
      title: "Preferences",
      items: [
        { label: "Audio Coach", to: "/settings/audio", testid: "settings-audio-link" },
        { label: "Notifications", to: "/settings/notifications", testid: "settings-notifs-link" },
        { label: "Habit & Privacy", to: "/settings/privacy", testid: "settings-privacy-link" },
        { label: "Data & Storage", to: "/settings/data", testid: "settings-data-link" },
      ],
    },
    {
      title: "Connected Accounts",
      items: [
        {
          label: "Google",
          value: isGoogle ? "Connected" : "Not connected",
          hint: isGoogle
            ? `Signed in as ${user?.email}`
            : "Sign in with Google next time for faster access",
          testid: "setting-google",
        },
        { label: "Connected Devices", to: "/settings/devices", testid: "settings-devices-link" },
      ],
    },
    {
      title: "Admin",
      adminOnly: true,
      items: [
        { label: "Verify Professionals", to: "/admin/professionals", testid: "settings-admin-pros" },
        { label: "Send Broadcast", to: "/admin/broadcast", testid: "settings-admin-broadcast" },
      ],
    },
    {
      title: "For Gyms",
      items: [
        { label: "List your gym on FitFrik", to: "/arena/join", testid: "settings-gym-join" },
        { label: "Arena — Find a gym", to: "/arena/find", testid: "settings-arena-find" },
      ],
    },
    {
      title: "Subscription",
      items: [
        { label: "Current plan", value: user?.subscription_tier?.toUpperCase() || "FREE" },
        { label: "Manage subscription", to: "/pricing", testid: "setting-manage-subscription" },
      ],
    },
    {
      title: "Legal",
      items: [
        { label: "Privacy Policy", to: "/legal/privacy", testid: "setting-privacy-policy" },
        { label: "Terms of Service", to: "/legal/terms", testid: "setting-terms" },
        { label: "Refund Policy", to: "/legal/refund", testid: "setting-refund" },
      ],
    },
  ];

  return (
    <div className="app-page" data-testid="settings-page">
      <TopBar title="Settings" />
      <main className="max-w-2xl mx-auto px-4 pt-3 pb-8 space-y-5">
        {/* Profile card */}
        <section
          className="bg-white rounded-[16px] p-4 border border-line2 flex items-center gap-3"
          style={{ boxShadow: "var(--shadow-soft)" }}
        >
          <div className="w-14 h-14 rounded-full overflow-hidden bg-canvas2 grid place-items-center">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-[22px]">
                {(user?.name || user?.email || "F")[0].toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[18px] truncate">{user?.name || "Guest"}</div>
            <div className="text-[12.5px] text-ink-soft truncate">{user?.email}</div>
          </div>
          <span
            className="px-2.5 h-7 rounded-pill text-[11px] font-medium grid place-items-center uppercase"
            style={{ background: "var(--accent-blue-dim)", color: "var(--text-primary)", borderRadius: 100 }}
          >
            {user?.subscription_tier || "free"}
          </span>
        </section>

        {sections
          .filter((sec) => !sec.adminOnly || user?.role === "admin")
          .map((sec) => (
            <section key={sec.title}>
              <div className="text-[11px] uppercase tracking-wider text-ink-muted px-1 mb-2">
                {sec.title}
              </div>
              <div
                className="bg-white rounded-[16px] border border-line2 divide-y divide-line2 overflow-hidden"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                {sec.items.map((item) => {
                  const interactive = !!(item.to || item.action);
                  return (
                    <button
                      key={item.label}
                      onClick={() => (item.to ? navigate(item.to) : item.action?.())}
                      data-testid={item.testid || `setting-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      disabled={!interactive}
                      className="w-full flex items-start justify-between px-4 py-3 hover:bg-canvas2 transition-colors text-left disabled:opacity-100 disabled:cursor-default"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[14.5px]">{item.label}</div>
                        {item.hint && (
                          <div className="text-[11.5px] text-ink-soft mt-0.5 truncate">
                            {item.hint}
                          </div>
                        )}
                      </div>
                      <span className="flex items-center gap-1.5 text-[13px] text-ink-muted shrink-0 mt-0.5">
                        {item.rightChip && (
                          <span
                            className="inline-flex items-center gap-1 px-2 h-5 rounded-pill"
                            style={{
                              background: "rgba(0,212,255,0.18)",
                              color: "#00B0D5",
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.08em",
                              borderRadius: 100,
                            }}
                          >
                            <ShieldCheck size={10} /> {item.rightChip}
                          </span>
                        )}
                        {item.value}
                        {interactive && <ChevronRight size={16} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

        {/* Danger zone */}
        <section>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted px-1 mb-2">Danger zone</div>
          <div
            className="bg-white rounded-[16px] border border-line2 overflow-hidden"
            style={{ boxShadow: "var(--shadow-soft)" }}
          >
            <button
              onClick={() => setShowDelete(true)}
              data-testid="setting-delete-account"
              className="w-full flex items-center justify-between px-4 h-12 hover:bg-canvas2 transition-colors text-left"
            >
              <span className="text-[14.5px] flex items-center gap-2 text-red-500">
                <Trash2 size={16} /> Delete Account
              </span>
              <ChevronRight size={16} className="text-ink-muted" />
            </button>
          </div>
          <p className="text-[11.5px] text-ink-tertiary mt-2 px-1">
            Soft-deleted for 30 days, then permanently removed. See our{" "}
            <button
              onClick={() => navigate("/legal/privacy")}
              className="underline"
            >
              Privacy Policy
            </button>{" "}
            for details.
          </p>
        </section>

        <button
          onClick={async () => {
            await logout();
            navigate("/login", { replace: true });
          }}
          data-testid="logout-btn"
          className="w-full bg-white rounded-[14px] py-4 flex items-center justify-center gap-2 border border-line2 text-red-500 font-medium"
        >
          <LogOut size={16} /> Sign out
        </button>

        <div className="text-center text-[11px] text-ink-tertiary pt-2">
          FitFrik · v1.0.0
        </div>
      </main>

      <BottomNav />

      {showChangePw && (
        <ChangePasswordSheet
          hasPassword={hasPassword}
          onClose={() => setShowChangePw(false)}
          onSuccess={() => {
            setShowChangePw(false);
            setHasPassword(true);
            checkAuth?.();
          }}
        />
      )}

      {showDelete && (
        <DeleteAccountModal
          hasPassword={hasPassword}
          onClose={() => setShowDelete(false)}
          onDeleted={async () => {
            setShowDelete(false);
            await logout();
            navigate("/login", { replace: true });
          }}
        />
      )}
    </div>
  );
}
